import { clockOf } from './gamingDay.ts';

/**
 * How the rooms table shows a room's zone, gaming day and internal flag (ADR 0012 step 6). Pure, so
 * the formatting is tested rather than eyeballed — an offset that is an hour out in winter looks
 * exactly as plausible as the right one.
 */

/**
 * Where the room is, as a person scanning the column looks for it: the id's last segment, with its
 * underscores as spaces. The full id is the tooltip; this is the part somebody reads.
 */
export const zoneCity = (zone: string): string => {
  const last = zone.split('/').pop() ?? zone;
  return last.replaceAll('_', ' ');
};

/**
 * The zone's offset from UTC at <code>at</code> — "UTC−7", "UTC+5:30", or "UTC" — computed in the
 * browser for the instant given, because it moves twice a year and is never stored. The minus is
 * U+2212. <b>Null for a zone this browser does not know</b>: the server validates against its own
 * timezone database, which may be newer, and an invented offset beside a real room is worse than a
 * blank.
 */
export const utcOffsetLabel = (zone: string, at: Date): string | null => {
  let name: string | undefined;
  try {
    name = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' })
      .formatToParts(at)
      .find((part) => part.type === 'timeZoneName')?.value;
  } catch {
    return null;
  }
  return name === undefined ? null : offsetLabelFromName(name);
};

/**
 * Intl's offset text, as the column words it. <b>The text is not fixed</b>: a zero offset arrives as "GMT"
 * from Node 25 (ICU 77) and as "GMT+00:00" from the Node 22 CI runner, and a browser may say "UTC",
 * "GMT+0", "+00:00" or "Z" — so every zero is "UTC", never "UTC+0", and the non-zero forms are read
 * whatever the prefix or separator. Text that is not an offset is null rather than a guess.
 */
export const offsetLabelFromName = (name: string): string | null => {
  if (name === 'Z') return 'UTC';
  const match = /^(GMT|UTC)?(?:([+-])(\d{1,2})(?::?(\d{2}))?)?$/.exec(name);
  if (!match || (match[1] === undefined && match[2] === undefined)) return null;
  if (match[2] === undefined) return 'UTC';

  const hours = Number(match[3]);
  const minutes = Number(match[4] ?? '0');
  if (hours > 14 || minutes > 59) return null;
  if (hours === 0 && minutes === 0) return 'UTC';

  const sign = match[2] === '-' ? '−' : '+';
  return `UTC${sign}${hours}${minutes === 0 ? '' : `:${String(minutes).padStart(2, '0')}`}`;
};

/** "Midnight" for a day that follows the calendar, otherwise the clock time it starts. */
export const gamingDayLabel = (minutes: number): string => (minutes === 0 ? 'Midnight' : clockOf(minutes));

/**
 * The rooms the table shows, and how many internal ones it left out. The count is what lets the
 * empty state tell "no rooms" from "every room is hidden", and lets the filter say what it is doing
 * rather than making rooms appear to have gone.
 */
export const visibleRooms = <T extends { isInternal: boolean }>(rooms: readonly T[], showInternal: boolean) => {
  const shown = showInternal ? [...rooms] : rooms.filter((room) => !room.isInternal);
  return { shown, hidden: rooms.length - shown.length };
};
